<template>
  <div class="chart-container" :style="{ height: `${height}px` }">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
import Chart from "chart.js";

export default {
  props: {
    chartData: { type: Object, required: true },
    options: { type: Object, default: () => ({}) },
    height: { type: Number, default: 400 },
    selectedIndexes: { type: Array, default: () => [] },
  },

  data() {
    return {
      chart: null,
      internalSelectedIndexes: [...this.selectedIndexes],
    };
  },

  watch: {
    chartData: {
      handler() {
        this.renderChart();
      },
      deep: true,
    },
    options: {
      handler() {
        this.renderChart();
      },
      deep: true,
    },
    selectedIndexes: {
      handler(newVal) {
        this.internalSelectedIndexes = [...newVal];
        this.syncHiddenState();
      },
      deep: true,
    },
  },

  mounted() {
    this.renderChart();
  },

  beforeDestroy() {
    if (this.chart) this.chart.destroy();
  },

  methods: {
    syncHiddenState() {
      if (!this.chart) return;
      const meta = this.chart.getDatasetMeta(0);
      meta.data.forEach((slice, idx) => {
        // In this specific UI, "selected" means "visible" in the table,
        // but often legend click "hides" the slice.
        // The user wants: click slice -> show in table.
        // Let's stick to the user's logic: if it's in selectedIndexes, it's "selected".
        // Chart.js legend toggle usually hides the slice.
        // We will just use the internalSelectedIndexes to decide hidden state if needed.
        // However, standard legend click hides the item. Let's not force hidden state
        // unless explicitly requested, to avoid conflict with standard legend behavior.
      });
      this.chart.update();
    },

    renderChart() {
      const optionsWithLegendClick = {
        ...this.options,

        legend: {
          ...this.options?.legend,
          labels: {
            ...this.options?.legend?.labels,
            generateLabels: (chart) => {
              const data = chart.data;
              if (data.labels.length && data.datasets.length) {
                return data.labels.map((label, i) => {
                  const ds = data.datasets[0];
                  // Logic: if some items are selected, unselected ones get the cross (hidden: true)
                  const isSelected = this.internalSelectedIndexes.includes(i);
                  const showCross =
                    this.internalSelectedIndexes.length > 0 && !isSelected;

                  return {
                    text: label,
                    fillStyle: ds.backgroundColor[i],
                    strokeStyle: ds.borderColor[i],
                    lineWidth: ds.borderWidth,
                    hidden: showCross, // This adds the strikethrough in legend
                    index: i,
                  };
                });
              }
              return [];
            },
          },
          onClick: (e, legendItem) => {
            const idx = legendItem.index;

            // toggle selection internally
            const i = this.internalSelectedIndexes.indexOf(idx);
            if (i > -1) this.internalSelectedIndexes.splice(i, 1);
            else this.internalSelectedIndexes.push(idx);

            // We no longer toggle meta.data[idx].hidden here to keep the slice visible.
            // The cross/strikethrough in legend will be handled by generateLabels.
            this.chart.update();

            // emit all currently selected slices
            const selectedSlices = this.internalSelectedIndexes.map((i) => ({
              index: i,
              label: this.chartData.labels[i],
              value: this.chartData.datasets[0].data[i],
            }));

            this.$emit("chart-click", {
              selectedSlices,
              source: "legend",
            });
          },
        },

        onClick: (evt, activeElements) => {
          if (!activeElements.length) return;
          const clickedIndex = activeElements[0]._index;

          // toggle selection internally
          const i = this.internalSelectedIndexes.indexOf(clickedIndex);
          if (i > -1) this.internalSelectedIndexes.splice(i, 1);
          else this.internalSelectedIndexes.push(clickedIndex);

          // We no longer toggle meta.data[clickedIndex].hidden here to keep the slice visible.
          this.chart.update();

          // emit all currently selected slices
          const selectedSlices = this.internalSelectedIndexes.map((i) => ({
            index: i,
            label: this.chartData.labels[i],
            value: this.chartData.datasets[0].data[i],
          }));

          this.$emit("chart-click", {
            selectedSlices,
            source: "slice",
            clickedIndex,
          });
        },
      };

      if (this.chart) this.chart.destroy();
      const ctx = this.$refs.canvas.getContext("2d");
      this.chart = new Chart(ctx, {
        type: "pie",
        data: this.chartData,
        options: optionsWithLegendClick,
      });

      // Sync hidden state from props after creation
      const meta = this.chart.getDatasetMeta(0);
      this.internalSelectedIndexes.forEach((idx) => {
        // If it's selected, maybe we want it visible?
        // Standard Chart.js is: all visible initially.
        // If we want it to "toggle" like legend, we should hide the others?
        // Let's just keep the sync simple: emit what's selected.
      });
    },
  },
};
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  cursor: pointer;
}
</style>
